// @ts-nocheck
import { ModelsApi, ModelsModel, Configuration } from '@/generated/api';

export interface FilteredModel {
  id: string;
  name: string;
  hfid?: string;
  authorWallet?: string;
  maxContextWindow?: number;
  royaltyPrice?: string;
  hwPricePerInputToken?: string;
  hwPricePerOutputToken?: string;
  poolId?: number;
  tokenId?: string;
  pricePer1M?: number; // USD per 1M tokens
  tags?: string[];
  modelType?: number;
}

/**
 * Service for handling model-related operations
 */
export class ModelsService {
  private modelsApi: ModelsApi;
  private modelsCache: FilteredModel[] | null = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  constructor() {
    // Create a basic configuration for the ModelsApi
    const baseURL = process.env.REACT_APP_API_URL || 'https://api.swarmind.ai';
    const config = new Configuration({
      basePath: baseURL,
      baseOptions: {
        withCredentials: true,
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json',
        },
      },
    });
    this.modelsApi = new ModelsApi(config);
  }

  /**
   * Get all available models (text + image, all pools)
   */
  async getAvailableModels(): Promise<FilteredModel[]> {
    // Check cache first
    const now = Date.now();
    if (this.modelsCache && now - this.cacheTimestamp < this.CACHE_DURATION) {
      return this.modelsCache;
    }

    try {
      // Fetch all models from pool 0
      const response = await this.modelsApi.getModels({
        modelType: -1,  // -1 = all types (text and image)
        poolId: 0,      // Pool 0 only
      });
      const models = response.data || [];

      const filteredModels = models
        .map((model: ModelsModel) => {
          const authorRoyalty = parseFloat(model.royalty_price || '0');
          const hwIn = parseFloat(model.hw_price_per_input_token || '0');
          const hwOut = parseFloat(model.hw_price_per_output_token || '0');
          const pricePer1M = (authorRoyalty + hwIn + hwOut) * 0.001;

          return {
            id: (model as any).id || model.name || '', // some specs may not include id, fallback to name
            name: model.name || (model as any).id || 'Unknown Model',
            hfid: model.hfid,
            authorWallet: model.author_wallet,
            maxContextWindow: model.max_context_window,
            royaltyPrice: model.royalty_price,
            hwPricePerInputToken: model.hw_price_per_input_token,
            hwPricePerOutputToken: model.hw_price_per_output_token,
            poolId: model.pool_id,
            tokenId: model.token_id,
            pricePer1M,
            tags: (model as any).tags || [],
            modelType: model.model_type,
          } as FilteredModel;
        });

      // Cache the results
      this.modelsCache = filteredModels;
      this.cacheTimestamp = now;

      return filteredModels;
    } catch (error) {
      // Return fallback models for development
      return this.getFallbackModels();
    }
  }

  /**
   * Get only text models (model_type=0)
   * For use in text chat settings
   */
  async getTextModels(): Promise<FilteredModel[]> {
    const allModels = await this.getAvailableModels();
    return allModels.filter(model => model.modelType === 0);
  }

  /**
   * Get only image models (model_type=1)
   * For use in image generation settings
   */
  async getImageModels(): Promise<FilteredModel[]> {
    const allModels = await this.getAvailableModels();
    return allModels.filter(model => model.modelType === 1);
  }

  /**
   * Get a specific model by ID
   */
  async getModelById(modelId: string): Promise<FilteredModel | null> {
    const models = await this.getAvailableModels();
    return models.find(model => model.id === modelId) || null;
  }

  /**
   * Convert blockchain model ID to API model name
   * This handles the mapping from NFT model IDs to actual API model names
   */
  getModelNameFromId(modelId: string): string {
    if (modelId.startsWith('nft-')) {
      const apiModelName = 'tiger-gemma-9b-v1-i1';
      return apiModelName;
    }
    return modelId;
  }

  /**
   * Check if a model is available for chat
   */
  async isModelAvailable(modelId: string): Promise<boolean> {
    try {
      const models = await this.getAvailableModels();
      return models.some(model => model.id === modelId);
    } catch {
      return false;
    }
  }

  /**
   * Get fallback models for development/testing
   */
  private getFallbackModels(): FilteredModel[] {
    return [
      {
        id: 'tiger-gemma-9b-v1-i1',
        name: 'Tiger Gemma 9B v1',
        hfid: 'tiger-gemma-9b-v1-i1',
        maxContextWindow: 8192,
        pricePer1M: (0 + 0 + 0) * 0.001,
      },
      {
        id: 'gemma-9b',
        name: 'Gemma 9B',
        hfid: 'gemma-9b',
        maxContextWindow: 8192,
        pricePer1M: (0 + 0 + 0) * 0.001,
      },
    ];
  }

  /**
   * Get model suggestions based on use case
   */
  async getModelSuggestions(
    useCase: 'general' | 'coding' | 'creative' = 'general'
  ): Promise<FilteredModel[]> {
    const allModels = await this.getAvailableModels();
    return allModels;
  }

  /**
   * Clear the models cache to force a fresh fetch
   */
  clearCache(): void {
    this.modelsCache = null;
    this.cacheTimestamp = 0;
  }
}

// Global singleton instance
let modelsServiceInstance: ModelsService | null = null;

export const modelsService = (() => {
  if (!modelsServiceInstance) {
    modelsServiceInstance = new ModelsService();
  }
  return modelsServiceInstance;
})();
